import { DecisionTreeContent, DecisionTreeNode } from "@amzn/ask-legal-domain";

/**
 * Node classification within the decision tree graph.
 *
 * - "connected": Reachable from the root node via children links
 * - "orphan-root": Not reachable from root, and no parent among other orphans (top of a disconnected subtree)
 * - "orphan-child": Not reachable from root, but has a parent among other orphans (part of an orphan subtree)
 */
export type NodeClassification = "connected" | "orphan-root" | "orphan-child";

/**
 * Edge classification based on the connectivity of parent and child nodes.
 *
 * - "connected": Both parent and child are reachable from root (normal main-tree edge)
 * - "orphan": Both parent and child are orphans (edge within an orphan subtree)
 * - "cross-connect": Connected parent → orphan child (rare: parent links to an orphan — means orphan SHOULD be connected)
 * - "cross-orphan": Orphan parent → connected child (orphan references a connected node)
 */
export type EdgeClassification = "connected" | "orphan" | "cross-connect" | "cross-orphan";

export interface ClassifiedNode {
    node: DecisionTreeNode;
    classification: NodeClassification;
}

export interface ClassifiedEdge {
    parentId: string;
    childId: string;
    answer: string;
    childIndex: number;
    totalChildren: number;
    classification: EdgeClassification;
}

export interface GraphResult {
    /** All non-deleted nodes, classified by connectivity */
    nodes: ClassifiedNode[];
    /** All edges (parent→child), classified by connectivity of endpoints */
    edges: ClassifiedEdge[];
    /** Number of orphan nodes */
    orphanCount: number;
    /** Number of connected nodes (reachable from root) */
    connectedCount: number;
    /** Set of connected node IDs (for quick lookup) */
    connectedIds: Set<string>;
    /** Set of orphan node IDs (for quick lookup) */
    orphanIds: Set<string>;
    /** Map of nodeId → DecisionTreeNode for O(1) lookup */
    nodeMap: Map<string, DecisionTreeNode>;
}

/**
 * Unified graph builder: Single pass to classify all nodes and compute all edges.
 *
 * Replaces the previous multi-BFS approach that was scattered across 3 files:
 * - decision-tree-node-hook.tsx (buildNodesAndEdges): 4 separate BFS traversals
 * - elk-tree-layout.ts (calculateLayout): 1 BFS traversal
 *
 * This function does:
 * 1. Build nodeMap once (O(n))
 * 2. Single BFS from root → classify connected vs orphan
 * 3. Classify orphan nodes as root vs child
 * 4. Single pass over ALL nodes → emit ALL edges with classification
 *
 * Total complexity: O(n + e) where n = nodes, e = edges
 *
 * @param content DecisionTreeContent with allNodes and rootNodeId
 * @returns GraphResult with classified nodes, edges, and lookup structures
 */
export function buildGraph(content: DecisionTreeContent): GraphResult {
    // Step 0: Build nodeMap (O(n)), filtering out deleted nodes
    const nodeMap = new Map<string, DecisionTreeNode>();
    for (const node of content.allNodes) {
        if (!node.deleted) {
            nodeMap.set(node.id, node);
        }
    }

    // Step 1: BFS from root → find all connected (reachable) node IDs
    const connectedIds = new Set<string>();
    const rootNode = nodeMap.get(content.rootNodeId);

    if (rootNode) {
        const queue: string[] = [content.rootNodeId];
        while (queue.length > 0) {
            const id = queue.shift()!;
            if (connectedIds.has(id)) continue;
            if (!nodeMap.has(id)) continue;
            connectedIds.add(id);

            const node = nodeMap.get(id)!;
            for (const child of node.children) {
                if (nodeMap.has(child.nodeId) && !connectedIds.has(child.nodeId)) {
                    queue.push(child.nodeId);
                }
            }
        }
    }

    // Step 2: Classify all nodes
    const orphanIds = new Set<string>();
    const classifiedNodes: ClassifiedNode[] = [];

    // First pass: identify all orphan IDs
    for (const [id] of nodeMap) {
        if (!connectedIds.has(id)) {
            orphanIds.add(id);
        }
    }

    // Second pass: classify orphans as root vs child
    // An orphan-root has no parent among orphans; an orphan-child does
    const orphanChildIds = new Set<string>();
    for (const id of orphanIds) {
        const node = nodeMap.get(id)!;
        for (const child of node.children) {
            if (orphanIds.has(child.nodeId)) {
                orphanChildIds.add(child.nodeId);
            }
        }
    }

    for (const [id, node] of nodeMap) {
        if (connectedIds.has(id)) {
            classifiedNodes.push({ node, classification: "connected" });
        } else if (orphanChildIds.has(id)) {
            classifiedNodes.push({ node, classification: "orphan-child" });
        } else {
            classifiedNodes.push({ node, classification: "orphan-root" });
        }
    }

    // Step 3: Build ALL edges in a single pass — no BFS needed
    const classifiedEdges: ClassifiedEdge[] = [];

    for (const [id, node] of nodeMap) {
        const totalChildren = node.children.length;
        node.children.forEach((child, index) => {
            // Skip edges to deleted/missing nodes
            if (!nodeMap.has(child.nodeId)) return;

            const parentConnected = connectedIds.has(id);
            const childConnected = connectedIds.has(child.nodeId);

            let classification: EdgeClassification;
            if (parentConnected && childConnected) {
                classification = "connected";
            } else if (!parentConnected && !childConnected) {
                classification = "orphan";
            } else if (parentConnected && !childConnected) {
                // This shouldn't normally happen because BFS from root follows children,
                // so if parent is connected and has this child, child should also be connected.
                // But it can occur if there's a data inconsistency.
                classification = "cross-connect";
            } else {
                // Orphan parent → connected child (orphan references a node in the main tree)
                classification = "cross-orphan";
            }

            classifiedEdges.push({
                parentId: id,
                childId: child.nodeId,
                answer: child.answer,
                childIndex: index,
                totalChildren,
                classification
            });
        });
    }

    return {
        nodes: classifiedNodes,
        edges: classifiedEdges,
        orphanCount: orphanIds.size,
        connectedCount: connectedIds.size,
        connectedIds,
        orphanIds,
        nodeMap
    };
}
